import type { EntryListQuery } from '@taler/api-client'
import { computed, type ComputedRef } from 'vue'
import { useRoute, useRouter, type LocationQueryRaw } from 'vue-router'
import {
  parseAmountParam,
  parseDateParam,
  parseIdParam,
  parseKindParam,
  parseSourceParam,
  parseTagsParam,
  parseTextParam,
} from '../internal/query-params.ts'

/** Every filter this screen offers: no paging (`limit`/`cursor`), no display currency (app-wide), and no account-subtree flag (no control offers it). */
export type TransactionFilterQuery = Omit<
  EntryListQuery,
  'limit' | 'cursor' | 'displayCurrency' | 'includeDescendants'
>

/**
 * One property per filter, typed against the generated `EntryListQuery`. Adding a filter
 * means adding one key here and one control in `TransactionFilters.vue`; nothing else about
 * how filters compose or serialize changes.
 */
const FILTER_SHAPE: { readonly [K in keyof Required<TransactionFilterQuery>]: true } = {
  from: true,
  until: true,
  accountId: true,
  kind: true,
  source: true,
  minAmount: true,
  maxAmount: true,
  search: true,
  merchantId: true,
  tags: true,
}
const FILTER_KEYS = Object.keys(FILTER_SHAPE) as (keyof TransactionFilterQuery)[]

const PARSERS: {
  readonly [K in keyof TransactionFilterQuery]-?: (raw: unknown) => TransactionFilterQuery[K]
} = {
  from: parseDateParam,
  until: parseDateParam,
  accountId: parseIdParam,
  kind: parseKindParam,
  source: parseSourceParam,
  minAmount: parseAmountParam,
  maxAmount: parseAmountParam,
  search: parseTextParam,
  merchantId: parseIdParam,
  tags: parseTagsParam,
}

/** `undefined` is explicitly allowed (unlike `Partial<TransactionFilterQuery>`) so any key can be cleared. */
export type TransactionFilterPatch = {
  readonly [K in keyof TransactionFilterQuery]?: TransactionFilterQuery[K] | undefined
}

export interface TransactionFilterState {
  readonly filters: ComputedRef<TransactionFilterQuery>
  readonly activeCount: ComputedRef<number>
  setFilter(patch: TransactionFilterPatch): void
  clearAll(): void
}

function readRaw(route: { query: LocationQueryRaw }): Partial<Record<keyof TransactionFilterQuery, unknown>> {
  const out: Partial<Record<keyof TransactionFilterQuery, unknown>> = {}
  for (const key of FILTER_KEYS) out[key] = (route.query as Record<string, unknown>)[key]
  return out
}

/**
 * Every filter lives in the URL, so a filtered view can be linked, reloaded and navigated
 * back to. `setFilter` composes onto the current filter set rather than replacing it, and
 * clearing a key drops it from the URL entirely rather than writing an empty string.
 */
export function useTransactionFilters(): TransactionFilterState {
  const route = useRoute()
  const router = useRouter()

  const filters = computed<TransactionFilterQuery>(() => {
    const raw = readRaw(route)
    const out: Record<string, unknown> = {}
    for (const key of FILTER_KEYS) {
      const value = PARSERS[key](raw[key])
      if (value !== undefined) out[key] = value
    }
    return out as TransactionFilterQuery
  })

  const activeCount = computed(() => Object.keys(filters.value).length)

  function setFilter(patch: TransactionFilterPatch): void {
    const merged: Record<string, unknown> = { ...filters.value, ...patch }
    const next: LocationQueryRaw = { ...route.query }
    for (const key of FILTER_KEYS) {
      const value = merged[key]
      next[key] =
        value === undefined ? undefined : Array.isArray(value) ? [...(value as string[])] : String(value)
    }
    void router.replace({ query: next })
  }

  function clearAll(): void {
    const next: LocationQueryRaw = { ...route.query }
    for (const key of FILTER_KEYS) next[key] = undefined
    void router.replace({ query: next })
  }

  return { filters, activeCount, setFilter, clearAll }
}
